import { useAuth } from '@clerk/clerk-react'
import { useEffect, useId, useRef, useState, type FormEvent } from 'react'

import { AuthNotice } from '@/components/auth/AuthNotice'
import { Button } from '@/components/ui/Button'
import { FIELD, LABEL } from '@/components/ui/formFields'
import { useMe } from '@/context/MeProvider'
import { useAuthRedirect } from '@/hooks/useAuthRedirect'
import { ApiError } from '@/lib/api/client'
import { reportListing } from '@/lib/api/listings'
import { cn } from '@/lib/cn'

const REASON_PRESETS = [
  'Broken or expired link',
  'Spam or scam',
  'Misleading offer',
  'Wrong brand or category',
] as const

function reportError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 401) return 'Sign in to report this link.'
    if (err.status === 403 && err.detail === 'Email not verified') {
      return 'Verify your email to report a link.'
    }
    if (err.status === 403 && err.detail === 'Account is not active') {
      return 'Your account is suspended.'
    }
    if (err.status === 403 && err.detail === 'Cannot report your own listing') {
      return "You can't report your own link."
    }
    if (err.status === 404) return 'This link is no longer available.'
    if (err.status === 409) return "You've already reported this link."
    return err.detail
  }
  return "Couldn't send your report."
}

export function ReportSheet({
  listingId,
  brand,
  open,
  onClose,
  onReported,
}: {
  listingId: string
  brand: string
  open: boolean
  onClose: () => void
  onReported: () => void
}) {
  const formId = useId()
  const dialogRef = useRef<HTMLDialogElement>(null)
  const { isSignedIn, getToken } = useAuth()
  const { goToSignIn } = useAuthRedirect()
  const { canMutate, messageFor } = useMe()
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) {
      return
    }
    if (open && !dialog.open) {
      setReason('')
      setError(null)
      setSubmitting(false)
      dialog.showModal()
    } else if (!open && dialog.open) {
      dialog.close()
    }
  }, [open])

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)

    if (!isSignedIn) {
      goToSignIn()
      return
    }
    const gate = messageFor('report')
    if (gate) {
      setError(gate)
      return
    }

    const trimmed = reason.trim()
    if (!trimmed) {
      setError('Enter a reason')
      return
    }

    setSubmitting(true)
    try {
      await reportListing(listingId, trimmed, getToken)
      onReported()
      onClose()
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        goToSignIn()
      }
      setError(reportError(err))
      setSubmitting(false)
    }
  }

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby={`${formId}-title`}
      onClose={onClose}
      onClick={(event) => {
        if (event.target === dialogRef.current) {
          onClose()
        }
      }}
      className={cn(
        'border-glass-border bg-surface-container text-fg m-auto w-[min(100%,28rem)] rounded-lg border p-0',
        'shadow-glow backdrop:bg-black/60 open:flex open:flex-col',
      )}
    >
      <form
        onSubmit={(event) => {
          void handleSubmit(event)
        }}
        className="space-y-5 p-6"
      >
        <div className="space-y-2">
          <h2
            id={`${formId}-title`}
            className="font-display text-headline-md text-fg"
          >
            Report {brand}
          </h2>
          <p className="font-body text-body-sm text-fg-muted">
            Tell us what&apos;s wrong. Enough reports and we pull the link for
            review.
          </p>
          <AuthNotice action="report" />
        </div>

        <div className="flex flex-wrap gap-2">
          {REASON_PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              disabled={submitting}
              onClick={() => {
                setReason(preset)
              }}
              className={cn(
                'font-body text-label-sm min-h-11 rounded-full border px-3 transition-colors',
                reason === preset
                  ? 'border-primary-container/30 bg-primary-container/15 text-primary'
                  : 'border-glass-border text-fg-muted hover:text-fg bg-white/5',
              )}
            >
              {preset}
            </button>
          ))}
        </div>

        <div className="space-y-2">
          <label htmlFor={`${formId}-reason`} className={LABEL}>
            Reason
          </label>
          <textarea
            id={`${formId}-reason`}
            required
            rows={4}
            value={reason}
            onChange={(event) => {
              setReason(event.target.value)
            }}
            placeholder="What should we look at?"
            disabled={submitting}
            className={cn(FIELD, 'min-h-28 resize-y')}
          />
        </div>

        {error && (
          <p className="font-body text-body-sm text-error" role="alert">
            {error}
          </p>
        )}

        <div className="flex flex-wrap gap-2">
          {isSignedIn ? (
            <Button
              type="submit"
              variant="gradient"
              size="md"
              disabled={submitting || !canMutate}
            >
              {submitting ? 'Sending…' : 'Submit report'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="gradient"
              size="md"
              onClick={goToSignIn}
            >
              Sign in to report
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="md"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
        </div>
      </form>
    </dialog>
  )
}
